import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import {
  Users,
  BedDouble,
  Phone,
  HandHelping,
  AlertTriangle,
  Plus,
  FileText,
  ClipboardList,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

interface DashboardStats {
  activeShelterClients: number;
  activeOutreachClients: number;
  hotlineCallsThisMonth: number;
  bedsAvailable: number;
  bedsTotal: number;
  protectionOrdersExpiring: number;
}

interface RecentActivity {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  user: string;
}

const placeholderStats: DashboardStats = {
  activeShelterClients: 24,
  activeOutreachClients: 67,
  hotlineCallsThisMonth: 142,
  bedsAvailable: 8,
  bedsTotal: 32,
  protectionOrdersExpiring: 3,
};

const placeholderActivity: RecentActivity[] = [
  {
    id: "1",
    type: "intake",
    description: "New client intake completed for Jane D.",
    timestamp: "2 hours ago",
    user: "Sarah M.",
  },
  {
    id: "2",
    type: "hotline",
    description: "Hotline call logged - crisis intervention",
    timestamp: "3 hours ago",
    user: "Maria L.",
  },
  {
    id: "3",
    type: "shelter",
    description: "Bed assignment updated - Room 204",
    timestamp: "5 hours ago",
    user: "John K.",
  },
  {
    id: "4",
    type: "case_note",
    description: "Case note added for client #1042",
    timestamp: "Yesterday",
    user: "Sarah M.",
  },
  {
    id: "5",
    type: "legal",
    description: "Protection order filing for client #987",
    timestamp: "Yesterday",
    user: "David R.",
  },
];

export function DashboardPage() {
  const { user } = useAuth();

  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ["dashboard", "stats"],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: DashboardStats }>("/dashboard/stats");
        return res.data;
      } catch {
        return placeholderStats;
      }
    },
    placeholderData: placeholderStats,
  });

  const { data: activity } = useQuery<RecentActivity[]>({
    queryKey: ["dashboard", "activity"],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: RecentActivity[] }>("/dashboard/activity");
        return res.data;
      } catch {
        return placeholderActivity;
      }
    },
    placeholderData: placeholderActivity,
  });

  const statCards = [
    {
      label: "Shelter Clients",
      value: stats?.activeShelterClients ?? 0,
      icon: BedDouble,
      gradient: "from-blue-500 to-blue-600",
      bgIcon: "bg-blue-500/10",
      textIcon: "text-blue-500",
      href: "/clients?type=adult-shelter",
    },
    {
      label: "Outreach Clients",
      value: stats?.activeOutreachClients ?? 0,
      icon: HandHelping,
      gradient: "from-emerald-500 to-emerald-600",
      bgIcon: "bg-emerald-500/10",
      textIcon: "text-emerald-500",
      href: "/clients?type=outreach",
    },
    {
      label: "Hotline Calls",
      value: stats?.hotlineCallsThisMonth ?? 0,
      icon: Phone,
      gradient: "from-amber-500 to-orange-500",
      bgIcon: "bg-amber-500/10",
      textIcon: "text-amber-500",
      href: "/clients?type=hotline",
    },
    {
      label: "Beds Available",
      value: `${stats?.bedsAvailable ?? 0}/${stats?.bedsTotal ?? 0}`,
      icon: BedDouble,
      gradient: "from-violet-500 to-purple-600",
      bgIcon: "bg-violet-500/10",
      textIcon: "text-violet-500",
      href: "/shelter",
    },
    {
      label: "Orders Expiring",
      value: stats?.protectionOrdersExpiring ?? 0,
      icon: AlertTriangle,
      gradient:
        (stats?.protectionOrdersExpiring ?? 0) > 0
          ? "from-red-500 to-rose-600"
          : "from-green-500 to-emerald-600",
      bgIcon:
        (stats?.protectionOrdersExpiring ?? 0) > 0
          ? "bg-red-500/10"
          : "bg-green-500/10",
      textIcon:
        (stats?.protectionOrdersExpiring ?? 0) > 0
          ? "text-red-500"
          : "text-green-500",
      href: "/legal",
    },
  ];

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-8">
      {/* Welcome header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            {greeting}, {user?.firstName ?? "User"}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Here's what's happening across your agency today.
          </p>
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <Link to="/clients/new">
            <Button className="gap-2 shadow-md shadow-primary/20">
              <Plus className="h-4 w-4" />
              New Intake
            </Button>
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {statCards.map((stat) => (
          <Link key={stat.label} to={stat.href}>
            <Card className="card-hover group relative overflow-hidden border-0 bg-card shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-bold tracking-tight text-foreground">
                      {stat.value}
                    </p>
                  </div>
                  <div
                    className={cn(
                      "flex h-11 w-11 items-center justify-center rounded-xl transition-transform group-hover:scale-110",
                      stat.bgIcon,
                    )}
                  >
                    <stat.icon className={cn("h-5 w-5", stat.textIcon)} />
                  </div>
                </div>
                {/* Bottom gradient bar */}
                <div className={cn(
                  "absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r opacity-60 transition-opacity group-hover:opacity-100",
                  stat.gradient,
                )} />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent activity */}
        <Card className="lg:col-span-2 border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">Recent Activity</CardTitle>
                <CardDescription className="mt-0.5">Latest actions across the system</CardDescription>
              </div>
              <Link to="/admin" className="text-xs font-medium text-primary transition-colors hover:text-primary/80">
                View all
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {activity && activity.length > 0 ? (
              <ul className="space-y-1">
                {activity.map((item) => (
                  <li
                    key={item.id}
                    className="group flex items-start gap-3.5 rounded-xl px-3 py-3 transition-colors hover:bg-accent/50"
                  >
                    <ActivityIcon type={item.type} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {item.description}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.user} &middot; {item.timestamp}
                      </p>
                    </div>
                    <ArrowUpRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/0 transition-all group-hover:text-muted-foreground" />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No recent activity.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold">Quick Actions</CardTitle>
            <CardDescription className="mt-0.5">Common tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {[
              { label: "New Client Intake", icon: Plus, href: "/clients?new=1", color: "text-indigo-500" },
              { label: "Log Hotline Call", icon: Phone, href: "/clients?type=hotline&new=1", color: "text-amber-500" },
              { label: "Shelter Check-In", icon: BedDouble, href: "/shelter", color: "text-blue-500" },
              { label: "Run Report", icon: FileText, href: "/reports", color: "text-emerald-500" },
              { label: "View My Cases", icon: ClipboardList, href: "/cases", color: "text-violet-500" },
            ].map((action) => (
              <Link key={action.label} to={action.href}>
                <div className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all hover:bg-accent/50">
                  <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg bg-accent/50 transition-colors group-hover:bg-accent", )}>
                    <action.icon className={cn("h-4 w-4", action.color)} />
                  </div>
                  <span className="text-sm font-medium text-foreground">{action.label}</span>
                  <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-muted-foreground/0 transition-all group-hover:text-muted-foreground" />
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ActivityIcon({ type }: { type: string }) {
  const iconMap: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
    intake: { icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" },
    hotline: { icon: Phone, color: "text-amber-500", bg: "bg-amber-500/10" },
    shelter: { icon: BedDouble, color: "text-violet-500", bg: "bg-violet-500/10" },
    case_note: { icon: ClipboardList, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    legal: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-500/10" },
  };

  const config = iconMap[type] ?? { icon: FileText, color: "text-gray-500", bg: "bg-gray-500/10" };
  const Icon = config.icon;

  return (
    <div className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", config.bg)}>
      <Icon className={cn("h-4 w-4", config.color)} />
    </div>
  );
}
